// Import OCR queue payload type for union (used in QueuedEvent)
import type { OCRQueuePayload } from './ocr';
import type { DeliveryReadiness } from '../utils/delivery-readiness';

// ===========================================
// Box Status Types
// ===========================================

/**
 * The 11 box status codes, as a runtime array.
 *
 * A union type is erased before any test can read it, which is how five maps keyed by
 * `BoxStatusCode` came to be kept in step by hand with nothing able to check them.
 * `BoxStatusCode` is derived from this array, so no call site changes and
 * `__tests__/box-status-contract.test.ts` can iterate the real set.
 *
 * Canon: `.claude/context/domain/box-status-machine.md`. The backend holds the source of
 * truth in `storage-api/src/uk/co/kitkeeper/booking/models/BoxStatus.kt`, whose
 * `validTransitions` is **parameterised** by order type — see `VALID_TRANSITIONS` below
 * for why the flat map here is the right projection of it rather than a copy of it.
 */
export const BOX_STATUS_CODES = [
  'WCAC',    // With Customer Awaiting Collection
  'WCFC',    // With Customer Failed Collection
  'ITKK',    // In Transit To Kit Keeper
  'WKKU',    // With Kit Keeper Unloaded
  'WKKS',    // With Kit Keeper Stored
  'WKKDO',   // With Kit Keeper Due Out
  'WKKP',    // With Kit Keeper Picked
  'WKKHO',   // With Kit Keeper Hold Out
  'ITTC',    // In Transit To Customer
  'ITTKKFR', // In Transit To KK Failed Return
  'WC',      // With Customer (delivered)
] as const;

export type BoxStatusCode = typeof BOX_STATUS_CODES[number];

export type StatusOwner = 'CUSTOMER' | 'COURIER' | 'WAREHOUSE';

export interface BoxStatus {
  id: number;
  code: BoxStatusCode;
  name: string;
  description: string | null;
  owner: StatusOwner;
  sortOrder: number;
}

// ===========================================
// Customer Types
// ===========================================

export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneNumber?: string | null; // @deprecated Old field name; backend uses 'phone'
  email?: string;
}

// ===========================================
// Order Box Types
// ===========================================

export interface BarcodeComponents {
  customerId: number;
  orderId: number;
  boxId: number;
  checkDigit: number | null;
  boxIdFormatted: string;
  displayBoxId: string;
  base12: string;
  full13: string | null;
  isEan13: boolean;
}

export interface OrderBox {
  id: number;
  orderId: number;
  barcode: string | null;
  barcodeComponents?: BarcodeComponents | null;
  storageCost: number;
  shippingCost: number;
  purchaseCost: number;
  currentStatusId: number | null;
  warehouseLocation: string | null;
  lastStatusUpdate: number | null;
  currentStatus: BoxStatus | null;
}

// ===========================================
// Address Types
// ===========================================

export interface Address {
  university?: string;
  streetNumber?: string;
  address1: string;
  address2?: string;
  city: string;
  county?: string;
  postcode: string;
  phone?: string;
  latitude?: string;
  longitude?: string;
}

// ===========================================
// Order Types
// ===========================================

export interface Order {
  id: number;
  customerId: number;
  city: string;                        // City from backend
  university: string | null;           // University name, for student storage orders
  collectionAddress: string;
  deliveryAddress: string | null;
  collectionDate: number;
  deliveryDate: number | null;
  collectionEta: string | null;
  deliveryEta: string | null;
  storageRequired: boolean;            // Backend field - app only handles storage orders (true)
  suitcaseOrder?: boolean;             // Special order type
  assignedDriverId: number | null;     // Kept for backward compat (equals collectionDriverId)
  assignedPartnerId: number | null;
  collectionDriverId: number | null;   // Driver assigned for collection leg
  deliveryDriverId: number | null;     // Driver assigned for delivery leg
  collectionCompletedAt: number | null;
  deliveryCompletedAt: number | null;
  collectionSignatureUrl: string | null;
  deliverySignatureUrl: string | null;
  collectionProofPhotoUrl: string | null;  // Photo taken after collection scan
  deliveryProofPhotoUrl: string | null;    // Photo taken after delivery scan
  orderBoxes: OrderBox[];
  customer: Customer | null;
}

// ===========================================
// Partner Types
// ===========================================

export type PartnerType = 'COURIER' | 'WAREHOUSE' | 'BOTH';

export interface Partner {
  id: number;
  name: string;
  type: PartnerType;
  billingRate?: number;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  logisticsDashboardEnabled?: boolean;
}

// ===========================================
// Partner User Types
// ===========================================

export type PartnerRole = 'MASTER' | 'DRIVER' | 'WAREHOUSE_STAFF';

export interface PartnerUser {
  id: number;
  partnerId: number;
  email: string;
  name: string;
  role: PartnerRole;
  phone: string | null;
  isActive: boolean;
  logisticsDashboardEnabled?: boolean;
  // v2.10 — "logistics controller" capability. When true, the user can
  // split/unsplit orders, override route stops, and lock/unlock days even
  // if they are not a partner master. Masters always have this implicitly.
  canControlLogistics?: boolean;
  jwt?: string; // Added for storing token with user
}

// ===========================================
// Status History Types
// ===========================================

export interface OrderBoxStatusHistory {
  id: number;
  orderBoxId: number;
  orderId: number;
  customerId: number;
  previousStatusId: number | null;
  newStatusId: number;
  triggeredByUserId: number | null;
  triggeredByRole: string | null;
  triggeredByPartnerId: number | null;
  latitude: number | null;
  longitude: number | null;
  warehouseLocation: string | null;
  notes: string | null;
  photoUrl: string | null;
  signatureUrl: string | null;
  createdAt: number;
  clientTimestamp: number | null;
  syncedAt: number | null;
}

// ===========================================
// Request Types
// ===========================================

export interface PartnerLoginRequest {
  email: string;
  password: string;
}

export interface StatusUpdateRequest {
  orderBoxId: number;
  newStatusCode: BoxStatusCode;
  latitude?: number;
  longitude?: number;
  warehouseLocation?: string;
  notes?: string;
  photoUrl?: string;       // Pre-uploaded URL (takes precedence over photoBase64)
  photoBase64?: string;    // Raw base64; server auto-uploads to S3
  signatureUrl?: string;
  clientTimestamp?: number;
}

export interface BatchStatusSyncRequest {
  events: StatusUpdateRequest[];
}

export interface SignatureRequest {
  signatureUrl: string;
  isCollection: boolean;
  proofPhotoUrl?: string;    // Pre-uploaded proof photo URL
  proofPhotoBase64?: string; // Raw base64; server auto-uploads to S3
}

export interface DriverAssignmentRequest {
  driverId?: number;              // Single assignment shorthand (sets both legs to same driver)
  collectionDriverId?: number;    // Split: collection courier
  deliveryDriverId?: number;      // Split: delivery courier
}

export interface EtaUpdateRequest {
  collectionEta?: string;
  deliveryEta?: string;
}

export interface AddBoxToOrderRequest {
  barcode?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
}

export interface RemoveBoxFromOrderRequest {
  reason?: string;
  latitude?: number;
  longitude?: number;
}

// ===========================================
// Response Types
// ===========================================

export interface ApiResponse {
  statusCode: string;
}

export interface PartnerLoginResponse extends ApiResponse {
  jwt: string;
  user: PartnerUser;
  partner: Partner;
}

// Coordinates side-channel from order_coordinates table
export interface OrderCoordinates {
  collectionLatitude: number | null;
  collectionLongitude: number | null;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
}

export interface CourierRoutesResponse extends ApiResponse {
  orders: Order[];
  date: string;
  orderCoordinates?: Record<number, OrderCoordinates> | null;
  warehouse?: Warehouse | null;
  stopOrder?: string[] | null;
}

export interface WarehouseIncomingResponse extends ApiResponse {
  orders: Order[];
  date: string;
}

export interface WarehouseOutgoingResponse extends ApiResponse {
  orders: Order[];
  date: string;
  dpdShipments?: Record<number, DpdShipmentSummary>;
}

export type DpdTrackingStatus = 'collected' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed';

export interface DpdShipmentSummary {
  trackingNumber: string | null;
  parcelNumber: string | null;
  trackingStatus: DpdTrackingStatus | null;
  shipmentType: string;
  collectionCode: string | null;
}

export interface OrderBoxResponse extends ApiResponse {
  orderBox: OrderBox;
}

export interface StatusUpdateResponse extends ApiResponse {
  historyEntry: OrderBoxStatusHistory;
}

export interface BatchSyncResponse extends ApiResponse {
  processed: number;
  failed: FailedSyncEvent[];
}

export interface FailedSyncEvent {
  event: StatusUpdateRequest;
  error: string;
}

export interface BoxStatusesResponse extends ApiResponse {
  statuses: BoxStatus[];
}

export interface CurrentUserResponse extends ApiResponse {
  user: PartnerUser;
  partner: Partner;
}

export interface PartnerUsersResponse extends ApiResponse {
  users: PartnerUser[];
}

export interface AddBoxToOrderResponse extends ApiResponse {
  orderBox: OrderBox;
}

export interface RemoveBoxFromOrderResponse extends ApiResponse {
  message: string;
  orderBoxId: number;
}

export interface PhotoUploadRequest {
  imageBase64: string;
  orderId?: number;
}

export interface PhotoUploadResponse extends ApiResponse {
  photoUrl: string;
}

// ===========================================
// Warehouse Types
// ===========================================

export interface Warehouse {
  id: number;
  partnerId: number;
  name: string;
  address: string;
  city: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface WarehousesResponse extends ApiResponse {
  warehouses: Warehouse[];
}

export interface WarehouseOrdersResponse extends ApiResponse {
  orders: Order[];
  date: string;
  type: 'incoming' | 'outgoing';
}

// ===========================================
// Admin Logistics Types
// ===========================================

export interface AvailableCouriersResponse extends ApiResponse {
  couriers: PartnerUser[];
}

export interface OrderAssignmentResponse extends ApiResponse {
  order: Order;
  courier: PartnerUser | null;
  courierPartner: Partner | null;
  warehouse: Warehouse | null;
  collectionCourier: PartnerUser | null;
  deliveryCourier: PartnerUser | null;
}

export interface AdminAssignRequest {
  courierId?: number;              // Same courier for both legs
  collectionCourierId?: number;    // Split: collection courier
  deliveryCourierId?: number;      // Split: delivery courier
  warehouseId?: number;
}

// ===========================================
// Offline Queue Types
// ===========================================

export type QueuedEventType =
  | 'STATUS_CHANGE'
  | 'SIGNATURE'
  | 'OCR_REQUEST'
  | 'ADD_BOX'
  | 'REMOVE_BOX'
  | 'ROUTE_ORDER';

export interface SignatureQueuePayload {
  orderId: number;
  isCollection: boolean;
  signatureBlobRef: string;
  proofPhotoBlobRef?: string;
  clientTimestamp: number;
}

export interface AddBoxQueuePayload {
  orderId: number;
  tempBoxId: number;
  barcode?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
  clientTimestamp: number;
}

export interface RemoveBoxQueuePayload {
  orderId: number;
  orderBoxId: number;
  reason?: string;
  latitude?: number;
  longitude?: number;
  clientTimestamp: number;
}

export interface RouteOrderQueuePayload {
  date: string;
  stopOrder: string[];
  clientTimestamp: number;
}

export type QueuedEventPayload =
  | StatusUpdateRequest
  | SignatureRequest
  | OCRQueuePayload
  | SignatureQueuePayload
  | AddBoxQueuePayload
  | RemoveBoxQueuePayload
  | RouteOrderQueuePayload;

export interface QueuedEvent {
  id: string;
  type: QueuedEventType;
  payload: QueuedEventPayload;
  createdAt: number;
  attempts: number;
  lastError?: string;
  failureCode?: string;
  requestId?: string;
  orderId?: number;
  tempBoxId?: number;
  blobRefs?: string[];
}

export interface OfflineQueue {
  events: QueuedEvent[];
  lastSyncAttempt: number | null;
  syncInProgress: boolean;
}

// ===========================================
// Status ID Mappings (Backend uses integer IDs)
// ===========================================

/**
 * Maps backend status IDs to status codes.
 * The backend returns currentStatusId (integer), not currentStatus (object).
 * Based on ProductDesignDocument.md status definitions.
 */
export const STATUS_ID_TO_CODE: Record<number, BoxStatusCode> = {
  1: 'WCAC',    // With Customer Awaiting Collection
  2: 'WCFC',    // With Customer Failed Collection
  3: 'ITKK',    // In Transit To Kit Keeper
  4: 'WKKU',    // With Kit Keeper Unloaded (skipped)
  5: 'WKKS',    // With Kit Keeper Stored
  6: 'WKKDO',   // With Kit Keeper Due Out
  7: 'WKKP',    // With Kit Keeper Picked
  8: 'WKKHO',   // With Kit Keeper Hold Out
  9: 'ITTC',    // In Transit To Customer
  10: 'ITTKKFR', // In Transit To KK Failed Return
  11: 'WC',     // With Customer (delivered)
};

/**
 * Maps status codes to backend status IDs.
 * Use when sending status updates to the backend.
 */
export const STATUS_CODE_TO_ID: Record<BoxStatusCode, number> = {
  'WCAC': 1,
  'WCFC': 2,
  'ITKK': 3,
  'WKKU': 4,
  'WKKS': 5,
  'WKKDO': 6,
  'WKKP': 7,
  'WKKHO': 8,
  'ITTC': 9,
  'ITTKKFR': 10,
  'WC': 11,
};

// Status code sort order for determining progress
export const STATUS_CODE_ORDER: Record<BoxStatusCode, number> = {
  'WCAC': 0,
  'WCFC': 0,
  'ITKK': 1,
  'WKKU': 2,
  'WKKS': 3,
  'WKKDO': 3,
  'WKKP': 4,
  'WKKHO': 4,
  'ITTC': 5,
  'ITTKKFR': 5,
  'WC': 6
};

// Human-readable display names for status codes
export const STATUS_CODE_DISPLAY_NAMES: Record<BoxStatusCode, string> = {
  'WCAC': 'Awaiting Collection',
  'WCFC': 'Failed Collection',
  'ITKK': 'In Transit to Warehouse',
  'WKKU': 'At Warehouse (Unloaded)',
  'WKKS': 'In Storage',
  'WKKDO': 'Due for Return',
  'WKKP': 'Picked for Dispatch',
  'WKKHO': 'Ready for Courier',
  'ITTC': 'Out for Delivery',
  'ITTKKFR': 'Failed Return',
  'WC': 'Delivered'
};

// ===========================================
// Workflow Status Configuration
// ===========================================

/**
 * Actionable status codes for each workflow context.
 * Boxes with these statuses can be acted upon in the given workflow.
 */
export type WorkflowType = 
  | 'collection'
  | 'collection-back'
  | 'delivery' 
  | 'warehouse-receive'
  | 'warehouse-store'
  | 'warehouse-pick'
  | 'warehouse-load'
  | 'courier-load';

export const ACTIONABLE_STATUSES: Record<WorkflowType, BoxStatusCode[]> = {
  'collection': ['WCAC', 'WCFC'],
  'collection-back': ['WC'],              // ONS/DPD: collect delivered boxes back from customer
  'delivery': ['ITTC'],
  'warehouse-receive': ['ITKK', 'ITTKKFR'],  // ITTKKFR re-enters warehouse at receive step
  'warehouse-store': ['WKKU'],
  'warehouse-pick': ['WKKS', 'WKKDO'],
  'warehouse-load': ['WKKP', 'WKKHO'],
  'courier-load': ['WKKP', 'WKKHO'],
};

/**
 * Target status code for each workflow action.
 * This is what boxes transition TO after the workflow action is complete.
 */
export const TARGET_STATUS: Record<WorkflowType, BoxStatusCode> = {
  'collection': 'ITKK',
  'collection-back': 'ITKK',
  'delivery': 'WC',
  'warehouse-receive': 'WKKU',
  'warehouse-store': 'WKKS',
  'warehouse-pick': 'WKKP',
  'warehouse-load': 'ITTC',
  'courier-load': 'ITTC',
};

/**
 * Valid status transitions — the scanner's projection of the backend's rules.
 *
 * **This is not a mirror of the backend map, and making it one would break the shipping
 * flow.** `BoxStatusCode.validTransitions` in `storage-api` is parameterised:
 *
 *   WCAC    -> isStorageOrder ? [ITKK, WCFC] : [ITTC, WCFC]
 *   WCFC    -> isStorageOrder ? [ITKK, WCAC] : [ITTC, WCAC]
 *   ITTKKFR -> isStorageOrder ? [ITKK, ITTC] : [ITTC]
 *   WC      -> allowCollectionBack ? [ITKK] : []
 *
 * The flat map below is that function evaluated at `isStorageOrder = true` and
 * `allowCollectionBack = true`, which is sound for this app for two reasons that live
 * elsewhere in the code rather than here:
 *
 *  - **Storage orders only.** `use-processed-routes.ts` and `Home.tsx` both drop orders
 *    with `storageRequired === false`, so a non-storage order never reaches a screen that
 *    can change a status. The `ITTC` arrows the backend offers those orders are therefore
 *    unreachable from the scanner, and the `ITKK` arrows this map offers are correct for
 *    everything it does see.
 *  - **Collection-back is gated server-side.** `WC -> ITKK` is accepted only when the order
 *    has an active DPD collection label; `LogisticsRepository` checks that on both the
 *    status-update and warehouse-receive paths and refuses with a message naming the
 *    reason. This map is deliberately the looser of the two: a client guard that fails
 *    open against an authoritative server cannot block a legitimate scan, which is the
 *    right direction for it to be wrong in.
 *
 * Both invariants are asserted in `__tests__/box-status-contract.test.ts`, because neither
 * is visible from this file.
 */
export const VALID_TRANSITIONS: Record<BoxStatusCode, BoxStatusCode[]> = {
  'WCAC': ['ITKK', 'WCFC'],            // Customer → Collected OR Failed Collection
  'WCFC': ['ITKK', 'WCAC'],            // Failed → retry collected OR revert to awaiting
  'ITKK': ['WKKU'],                    // In Transit → Unloaded at warehouse
  'WKKU': ['WKKS'],                    // Unloaded → Stored
  'WKKS': ['WKKDO', 'WKKP'],           // Stored → Due Out OR Picked
  'WKKDO': ['WKKP'],                   // Due Out → Picked
  'WKKP': ['WKKHO'],                   // Picked → Hold Out
  'WKKHO': ['ITTC'],                   // Hold Out → Out for Delivery
  'ITTC': ['WC', 'ITTKKFR'],           // Out for Delivery → Delivered OR Failed Return
  'ITTKKFR': ['ITKK', 'ITTC'],         // Failed Return → re-enter warehouse cycle OR revert to in-transit
  /**
   * ONS (Our Next Service) = DPD Integration — currently under development.
   * "collection-back" workflow: When a customer's boxes have been delivered (WC),
   * and a new DPD collection is scheduled, those boxes re-enter the warehouse cycle.
   * The courier collects them from the customer's delivery address → ITKK.
   * Backend only includes WC boxes in routes when an active DPD/ONS collection exists.
   */
  'WC': ['ITKK'],
};

/**
 * Check if a status transition is valid before making API call
 */
export const canTransitionTo = (currentCode: BoxStatusCode, targetCode: BoxStatusCode): boolean => {
  if (currentCode === targetCode) return false; // Already at target
  return VALID_TRANSITIONS[currentCode]?.includes(targetCode) ?? false;
};

/**
 * Phase-scope filters — which statuses belong to each leg of an order.
 *
 * A box's status says which leg it is on, and one order can hold boxes on both: a box that
 * failed collection (WCFC) never enters delivery, and a box already in the warehouse was
 * collected on an earlier trip. Counting the wrong set is what makes a dashboard card
 * disagree with the screen behind it.
 *
 * These were two inline literals — one declared inside a loop body in
 * `use-processed-routes.ts`, one an anonymous array beside it — so the canon's instruction
 * to apply them on every screen showing box counts could not actually be followed. They
 * are exported here so a second caller is a reuse rather than a fourth copy.
 */
export const COLLECTION_PHASE_STATUSES: BoxStatusCode[] = ['WCAC', 'ITKK', 'WCFC'];
export const DELIVERY_PHASE_STATUSES: BoxStatusCode[] = ['WKKHO', 'ITTC', 'WC', 'ITTKKFR'];

/**
 * Check if a box's status indicates it has completed the given workflow
 */
export const isStatusCompletedForWorkflow = (statusCode: BoxStatusCode, workflowType: WorkflowType): boolean => {
  const targetStatus = TARGET_STATUS[workflowType];
  if (!targetStatus) return false;
  return STATUS_CODE_ORDER[statusCode] >= STATUS_CODE_ORDER[targetStatus];
};

// ===========================================
// Warehouse Smart Scanning Helpers
// ===========================================

/**
 * Maps current warehouse status to the next status in the flow.
 * Used by smart scanning to auto-detect and apply the correct transition.
 */
export const WAREHOUSE_NEXT_STATUS: Partial<Record<BoxStatusCode, BoxStatusCode>> = {
  'ITKK': 'WKKU',     // Receive: In Transit → Unloaded
  'ITTKKFR': 'ITKK',  // Receive: Failed Return → re-enter at In Transit (handshake)
  'WKKU': 'WKKS',     // Store: Unloaded → Stored
  'WKKS': 'WKKP',     // Pick: Stored → Picked
  'WKKDO': 'WKKP',    // Pick: Due Out → Picked
  'WKKP': 'WKKHO',    // Hold Out: Picked → Hold Out
  'WKKHO': 'ITTC',    // Load: Hold Out → In Transit to Customer
};

/**
 * Gets the next status for a box in the warehouse flow.
 * Returns null if the box cannot be transitioned (invalid status or terminal state).
 */
export const getWarehouseNextStatus = (currentStatus: BoxStatusCode): BoxStatusCode | null => {
  return WAREHOUSE_NEXT_STATUS[currentStatus] || null;
};

/**
 * Determines which warehouse section a box belongs to based on its status.
 * Used for display grouping in the combined warehouse views.
 */
export type WarehouseSection = 'receive' | 'store' | 'pick' | 'holdout' | 'load' | 'none';

export const getWarehouseSection = (statusCode: BoxStatusCode): WarehouseSection => {
  switch (statusCode) {
    case 'ITKK':
      return 'receive';
    case 'WKKU':
      return 'store';
    case 'WKKS':
    case 'WKKDO':
      return 'pick';
    case 'WKKP':
      return 'holdout';
    case 'WKKHO':
      return 'load';
    default:
      return 'none';
  }
};

// ===========================================
// Processed Order Types
// ===========================================

/**
 * ProcessedOrder extends Order with runtime UI fields.
 * Uses backend fields directly without transformation.
 */
export interface ProcessedOrder extends Omit<Order, 'id'> {
  id: number;
  type: 'collection' | 'delivery';
  progress: number;
  signature: boolean;          // Local UI state for immediate feedback
  index: number;
  boxes: EnhancedOrderBox[];   // Processed boxes with workflow flags
  pendingWarehouseBoxes?: number; // Number of boxes awaiting warehouse prep (WKKDO/WKKP) - hidden from courier
  deliveryReadiness?: DeliveryReadiness; // Computed readiness state for delivery orders
  allBoxCount?: number; // Total boxes across ALL stages (before workflow filtering)
  routeIndex?: number; // Position in optimized route (undefined = no coords)
}

/**
 * Parses address string into display components.
 * API returns addresses as strings like "123 High Street, London, SW1 1AA"
 */
export const parseAddressForDisplay = (addressString: string | null): {
  address1: string;
  city: string;
  postcode: string;
} | null => {
  if (!addressString) return null;
  const parts = addressString.split(',').map(p => p.trim());
  return {
    address1: parts[0] || '',
    city: parts.length > 2 ? parts[parts.length - 2] : '',
    postcode: parts[parts.length - 1] || ''
  };
};

/**
 * Enhanced OrderBox with resolved status code and workflow flags.
 */
export interface EnhancedOrderBox extends OrderBox {
  statusCode: BoxStatusCode;  // Resolved from currentStatusId or currentStatus.code
  isActionable: boolean;      // Can this box be acted upon in current workflow?
  isCompleted: boolean;       // Has this box completed the current workflow?
  scanned?: boolean;          // Runtime flag: has this box been scanned in current session?
  isNewlyAdded?: boolean;     // Runtime flag: box was just added by courier in current session
  isPendingSync?: boolean;    // Runtime flag: box has a queued offline mutation awaiting sync
  routeIndex?: number;           // Parent stop's route position
  orderCustomerName?: string;    // For group header in load screen
}

/**
 * Enhanced OrderBox with parent order context (courier, collection date, customer).
 * Used in warehouse views where boxes are flattened from multiple orders.
 */
export interface EnhancedOrderBoxWithContext extends EnhancedOrderBox {
  assignedDriverId: number | null;   // Backward compat fallback
  collectionDriverId: number | null; // Driver assigned for collection
  deliveryDriverId: number | null;   // Driver assigned for delivery
  assignedPartnerId: number | null;
  collectionDate: number;
  deliveryDate: number | null;
  customerName: string | null;
}

/**
 * Resolves the status code from an OrderBox.
 * Handles both currentStatusId (integer) and currentStatus.code (string) formats.
 */
export const resolveBoxStatusCode = (orderBox: OrderBox): BoxStatusCode => {
  // First try the nested object (if backend includes it)
  if (orderBox.currentStatus?.code) {
    return orderBox.currentStatus.code as BoxStatusCode;
  }
  // Fall back to ID mapping
  if (orderBox.currentStatusId) {
    return STATUS_ID_TO_CODE[orderBox.currentStatusId] || 'WCAC';
  }
  // Default for new boxes without status
  return 'WCAC';
};

// ===========================================
// Partner Dashboard Types
// ===========================================

export interface ForecastCell {
  date: string;           // ISO "2026-03-18"
  displayDate: string;    // "18/03"
  dayName: string;        // "Wed"
  collectionsOrders: number;
  collectionsBoxes: number;
  returnsOrders: number;
  returnsBoxes: number;
}

export interface ForecastDetailOrder {
  orderId: number;
  customer: string;
  address: string;
  postcode: string | null;
  boxes: number;
  boxType: string | null;
}

export interface CollectionRow {
  id: number;
  driverId: number | null;
  driver: string | null;
  collectionDate: string;  // dd/MM/yyyy
  customer: string;
  order: string;
  boxesBooked: number;
  boxesCollected: number;
  address: string;
  postcode: string | null;
  /** @deprecated Use etaStart / etaEnd. Kept for backward compat with older payloads. */
  eta: string | null;
  /** Start of the 2-hour ETA window, "HH:mm". */
  etaStart: string | null;
  /** End of the 2-hour ETA window, "HH:mm". */
  etaEnd: string | null;
  /** Raw HubSpot timeslot value. Normalised for display via formatTimeslot(). */
  timeslot: string | null;
  doorstep: boolean;
  lastScan: string | null;
}

export interface ReturnRow {
  id: number;
  driverId: number | null;
  driver: string | null;
  returnDate: string;
  customer: string;
  order: string;
  boxesToReturn: number;
  boxesReturned: number;
  address: string | null;
  postcode: string | null;
  /** @deprecated Use etaStart / etaEnd. Kept for backward compat with older payloads. */
  eta: string | null;
  /** Start of the 2-hour ETA window, "HH:mm". */
  etaStart: string | null;
  /** End of the 2-hour ETA window, "HH:mm". */
  etaEnd: string | null;
  /** Raw HubSpot timeslot value. Normalised for display via formatTimeslot(). */
  timeslot: string | null;
  doorstep: boolean;
  lastScan: string | null;
}

export interface RoutePlanDayDTO {
  date: string;
  displayDate: string;
  dayName: string;
  locked: boolean;
  orders: RoutePlanOrderDTO[];
}

export interface RoutePlanOrderDTO {
  id: number;
  type: string;           // "C" or "R"
  customer: string;
  address: string | null;
  postcode: string | null;
  boxes: number;
  driver: string | null;
  driverId: number | null;
  eta: string | null;
  confirmed: boolean;
}

export interface DriverSummary {
  id: number;
  name: string;
}

/** Monetary values (rate, minCharge) are in pence — divide by 100 for display */
export interface InvoiceWeekDTO {
  weekCommencing: string;
  displayLabel: string;
  doorstepCollections: { boxes: number; rate: number };
  timeslotCollections: { boxes: number; rate: number };
  doorstepReturns: { boxes: number; rate: number };
  timeslotReturns: { boxes: number; rate: number };
  /** Failed-delivery fee charged per failed attempt. Null when partner has no such fee. */
  failedDeliveries?: { count: number; rate: number } | null;
  /**
   * Free-form extra fee lines configured per partner in the booking-portal admin console.
   * `quantity` is whatever unit the rate is per (boxes, jobs, hours, ...), `rate` is in pence.
   * `applyMode` is metadata returned by the backend for tooltip / display purposes.
   */
  additionalFees?: { label: string; quantity: number; rate: number; applyMode?: AdditionalFeeApplyMode }[];
  minCharge: number;      // pence
}

/** Metadata on how an additional-fee row was configured. Returned by the backend; informational only. */
export type AdditionalFeeApplyMode = 'FLAT' | 'PER_BOX' | 'PER_DELIVERY' | 'WEEKLY';

// ===========================================
// Warehouse Dashboard Types
// ===========================================

export interface DailyOverviewEntry {
  date: string;            // ISO
  inboundOrders: number;
  inboundBoxes: number;
  outboundOrders: number;
  outboundBoxes: number;
}

export interface WarehouseKPIs {
  todayInbound: { orders: number; boxes: number };
  todayOutbound: { orders: number; boxes: number };
  boxesInStorage: number;
}

export interface ForecastDataPoint {
  date: string;            // dd/MM/yyyy
  inbound: number;
  outbound: number;
}

/**
 * Warehouse "Future Movements" cell — same shape as ForecastCell but with
 * inbound / outbound metrics instead of collections / returns.
 */
export interface WarehouseForecastCell {
  date: string;            // ISO "2026-03-18"
  displayDate: string;     // "18/03"
  dayName: string;         // "Wed"
  inboundOrders: number;
  inboundBoxes: number;
  outboundOrders: number;
  outboundBoxes: number;
}

/**
 * Aggregated count for one workflow stage on an order.
 *   scanned: how many of `total` boxes have been scanned past the stage threshold.
 *   total:   how many boxes the order owes for this stage.
 */
export interface StageProgress {
  scanned: number;
  total: number;
}

export interface InboundOrderRow {
  oid: number;
  bid: string | null;
  orderBoxId: number;
  customer: string;
  courier: string | null;
  eta: string | null;
  /** End of the 2-hour ETA window if backend provides it; otherwise omit and `eta` is shown alone. */
  etaEnd?: string | null;
  status: string;
  statusCode: string;
  warehouseCode: string;
  collectionDate: string;
  dayOfOps: string;
  boxCount: number;
  boxType: string | null;
  /** "X of Y boxes unloaded" — boxes whose status has reached WKKU. */
  unloadProgress?: StageProgress;
  /** Issue flag(s) raised against this order/box. Surfaced as a badge in the row. */
  issues?: OrderIssue[];
  // TODO STORE: warehouse-to-location feature does not exist yet; add `storeProgress` once shipped.
}

export interface OutboundOrderRow {
  oid: number;
  bid: string | null;
  orderBoxId: number;
  customer: string;
  courier: string | null;
  deliveryDate: string;
  dayOfOps: string;
  status: string;
  statusCode: string;
  warehouseCode: string;
  boxCount: number;
  boxType: string | null;
  /** Boxes picked from storage (WKKS -> WKKP). */
  pickProgress?: StageProgress;
  /** Boxes set aside in hold-out area (WKKP -> WKKHO). */
  holdoutProgress?: StageProgress;
  /** Boxes handed over to ONS partner. */
  onsProgress?: StageProgress;
  /** Issue flag(s) raised against this order/box. */
  issues?: OrderIssue[];
}

export type OrderIssueType = 'damaged' | 'overweight' | 'repack' | 'other';

/**
 * Server-issued issue flag. `id` / `orderId` / `flaggedAt` / `flaggedBy` are
 * populated by the backend on responses; they are optional in the type so
 * the frontend can construct a temporary record for optimistic UI updates
 * before the server confirms the POST.
 */
export interface OrderIssue {
  id?: number;
  orderId?: number;
  type: OrderIssueType;
  /** Box ID the issue applies to, or null if the issue is on the whole order. */
  bid?: string | null;
  note?: string | null;
  flaggedAt?: string;
  flaggedBy?: number | null;
}

export interface StorageDataPoint {
  date: string;
  count: number;
}

/** Monetary values (amount) are in pence — divide by 100 for display */
export interface InvoiceLineItem {
  label: string;
  rate: string | null;
  quantity: number | null;
  amount: number;          // pence
}

export interface BoxHistoryRow {
  name: string;
  oid: number;
  bid: string | null;
  collectionDate: string | null;
  collectionCourier: string | null;
  lastScanDate: string | null;
  lastBoxStatus: string;
  warehouse: string | null;
  deliveryDate: string | null;
  deliveryCourier: string | null;
}
